import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Monitor, 
  Smartphone,
  CheckCircle2,
  ArrowRight,
  Layers,
  Activity,
  Cpu,
  Database,
  BarChart3,
  Rocket,
  Globe,
  ShieldCheck,
  Truck,
  Zap,
  Layout,
  Settings,
  Users,
  Briefcase,
  PieChart,
  ClipboardList,
  Languages
} from 'lucide-react';
import { Icon } from '@iconify/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DemoRequestModal } from '../../components/home/DemoRequestModal';
import { QuoteRequestModal } from '../../components/home/QuoteRequestModal';
import {
  HeroProductStage,
  CustomizationProductStage,
  ProductShowcase,
  ProductShot,
} from '../../components/home/ProductShots';
import { ThemeColors } from '../../styles/ThemeStyles';
import { SEO } from '../../components/common/SEO';

// ReactBits Components
import { SpotlightCard } from '../../components/animations/SpotlightCard';
import { VariableProximity } from '../../components/animations/VariableProximity';
import { Carousel } from '../../components/animations/Carousel';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
  color: #2d3748;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow-x: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 10% 20%, ${ThemeColors.primary.main}08 0%, transparent 40%),
      radial-gradient(circle at 90% 80%, ${ThemeColors.secondary.main}08 0%, transparent 40%);
    pointer-events: none;
    z-index: 0;
  }
`;

const Header = styled.header`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  padding: 0.75rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
`;

const LogoImg = styled.img`
  height: 45px;
`;

const NavLinks = styled.nav`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 968px) {
    gap: 0.75rem;
  }

  @media (max-width: 768px) {
    & > a, & > button:not(.lang-btn) {
      display: none;
    }
  }
`;

const NavLink = styled.a`
  color: #4a5568;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
  cursor: pointer;
  font-size: 0.95rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;

  &:hover {
    color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.main}08;
  }
`;

const LoginButton = styled.button`
  background-color: transparent;
  color: ${ThemeColors.primary.main};
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  border: 2px solid ${ThemeColors.primary.main};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: ${ThemeColors.primary.main};
    color: white;
    box-shadow: 0 4px 12px rgba(26, 66, 138, 0.2);
  }
`;

const LangSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  border-radius: 100px;
  background: white;
  border: 2px solid #e2e8f0;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.8rem;
  color: #4a5568;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);

  &:hover {
    border-color: ${ThemeColors.primary.main};
    color: ${ThemeColors.primary.main};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }

  svg {
    color: ${ThemeColors.primary.main};
  }
`;

// Hero Section
const HeroSection = styled.section`
  padding: 6rem 2rem 7rem;
  max-width: 1360px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 3.5rem;
  align-items: center;
  min-height: 80vh;
  position: relative;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 5rem 1.5rem 6rem;
    gap: 3rem;
  }
`;

const HeroContent = styled.div`
  z-index: 2;
  opacity: 1; /* Default to visible */
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${ThemeColors.primary.main}15;
  color: ${ThemeColors.primary.main};
  padding: 0.5rem 1rem;
  border-radius: 100px;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.5rem;
`;

const Headline = styled.h1`
  font-size: 4.5rem;
  font-weight: 900;
  line-height: 1.05;
  color: ${ThemeColors.primary.main};
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;

  .accent {
    color: ${ThemeColors.secondary.main};
  }

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const Subheadline = styled.p`
  font-size: 1.35rem;
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 3rem;
  max-width: 600px;

  @media (max-width: 1024px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1.25rem;
  
  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  background-color: ${ThemeColors.secondary.main};
  color: white;
  padding: 1.1rem 2.5rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.15rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 14px rgba(107, 176, 94, 0.3);

  &:hover {
    background-color: ${ThemeColors.secondary.dark};
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(107, 176, 94, 0.4);
  }
`;

const GhostButton = styled.button`
  background-color: white;
  color: ${ThemeColors.primary.main};
  padding: 1.1rem 2.5rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.15rem;
  border: 2px solid ${ThemeColors.primary.main};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: ${ThemeColors.primary.main}08;
    transform: translateY(-3px);
  }
`;

const HeroImageWrapper = styled.div`
  position: relative;
  opacity: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  
  &::before {
    content: '';
    position: absolute;
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, ${ThemeColors.primary.main}15 0%, transparent 70%);
    z-index: -1;
    filter: blur(40px);
  }
`;

const FloatingCard = styled.div<{ top?: string; left?: string; right?: string; bottom?: string }>`
  position: absolute;
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  background: white;
  padding: 1.25rem;
  border-radius: 16px;
  box-shadow: 0 15px 35px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 10;
  animation: float 6s ease-in-out infinite;

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Trust Bar
const TrustBar = styled.section`
  padding: 4rem 2rem;
  background: #f8fafc;
  border-top: 1px solid #edf2f7;
  border-bottom: 1px solid #edf2f7;
  text-align: center;
`;

const TrustTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 3rem;
`;

const LogoStrip = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5rem;
  opacity: 0.4;
  filter: grayscale(1);
  flex-wrap: wrap;
`;

// Features
const SectionContainer = styled.section`
  padding: 8rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  &#solutions {
    max-width: 1120px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionTitle = styled.h2<{ light?: boolean; left?: boolean }>`
  font-size: 3rem;
  font-weight: 800;
  color: ${props => props.light ? 'white' : ThemeColors.primary.main};
  margin-bottom: 1.5rem;
  text-align: ${props => props.left ? 'left' : 'inherit'};
  letter-spacing: -0.01em;
  position: relative;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const IconWrapper = styled.div<{ color: string }>`
  width: 70px;
  height: 70px;
  background-color: ${props => props.color}15;
  color: ${props => props.color};
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

// Interactivity / Content Blocks
const ContentBlock = styled.div<{ reverse?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
  margin-bottom: 10rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
    direction: ${props => props.reverse ? 'rtl' : 'ltr'};
    
    & > * {
      direction: ltr;
    }
  }
`;

// Testimonials
const TestimonialSection = styled.section`
  padding: 10rem 2rem;
  background: linear-gradient(135deg, #1a428a 0%, #163872 100%);
  color: white;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://www.transparenttextures.com/patterns/cubes.png');
    opacity: 0.05;
    pointer-events: none;
  }
`;

// Metrics
const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const MetricBox = styled.div`
  text-align: center;
  h3 { 
    font-size: 4.5rem; 
    font-weight: 900; 
    color: ${ThemeColors.primary.main}; 
    margin: 0;
    background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.secondary.main});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
  p { font-size: 1.25rem; color: #4a5568; font-weight: 700; margin-top: 0.5rem; }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  border-radius: 100px;
  font-weight: 700;
  border: 2px solid ${props => props.active ? ThemeColors.primary.main : '#edf2f7'};
  background: ${props => props.active ? ThemeColors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : '#718096'};
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    color: ${props => props.active ? 'white' : ThemeColors.primary.main};
  }
`;

const FeatureIconBox = styled.div<{ bg: string }>`
  width: 48px;
  height: 48px;
  background: ${props => props.bg};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 1rem;
`;

const CustomizationBadge = styled.div`
  background: linear-gradient(90deg, #6366f1 0%, #a855f7 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 100px;
  font-weight: 700;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 1rem;
`;

const Footer = styled.footer`
  background-color: #0f172a;
  color: white;
  padding: 6rem 2rem 2rem 2rem;
`;

const FooterGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FooterCol = styled.div`
  h4 { font-size: 1.1rem; font-weight: 700; margin-bottom: 1.5rem; }
  ul { list-style: none; padding: 0; }
  li { margin-bottom: 0.75rem; }
  a { color: #94a3b8; text-decoration: none; transition: color 0.2s; }
  a:hover { color: white; }
`;

const testimonialItems = [
  {
    title: "Marcus Thorne, COO of Global Agritech",
    description: "Nexis transformed our 40-year-old distribution model in just 6 months. Our field team is now 30% more efficient, and paper waste is zero.",
    id: 1
  },
  {
    title: "Sarah Jenkins, VP of Logistics at North-Seed Corp",
    description: "The real-time visibility into store visits and inventory levels was the missing piece of our puzzle. We now make data-driven decisions daily.",
    id: 2
  },
  {
    title: "David Miller, Director of Digital Transformation",
    description: "Customization was the key differentiator. Nexis didn't just give us software; they gave us a digital twin of our entire enterprise operations.",
    id: 3
  },
  {
    title: "Elena Rodriguez, Head of Distribution, Prime Logistics",
    description: "We've seen a 40% reduction in customer service inquiries because retailers now have real-time tracking for every single order.",
    id: 4
  },
  {
    title: "Thomas Chen, IT Director, Pacific Manufacturing",
    description: "The speed of implementation was incredible. We were able to migrate our entire sales force to the Nexis mobile engine without a single hour of downtime.",
    id: 5
  },
  {
    title: "Amanda West, Regional Sales Manager",
    description: "My reps love the interface. It's clean, fast, and works perfectly in remote rural areas where connectivity used to be our biggest hurdle.",
    id: 6
  }
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'manager' | 'salesrep' | 'core'>('manager');
  const [activeShot, setActiveShot] = useState(0);
  
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleLanguage = () => {
    const nextLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(nextLang);
  };

  const showcaseData: Record<'manager' | 'salesrep' | 'core', ProductShot[]> = {
    manager: [
      {
        title: t('landingPage.executiveDashboard'),
        description: t('landingPage.executiveDashboardDesc'),
        image: "/screenshots/Manager-Dashboard.png",
        icon: "solar:chart-2-bold-duotone",
        kind: 'desktop'
      },
      {
        title: t('landingPage.storeManagement'),
        description: t('landingPage.storeManagementDesc'),
        image: "/screenshots/Manager-Store-Management-.png",
        icon: "solar:shop-2-bold-duotone",
        kind: 'desktop'
      },
      {
        title: t('landingPage.shipmentLogistics'),
        description: t('landingPage.shipmentLogisticsDesc'),
        image: "/screenshots/Manager-Shipment-management-.png",
        icon: "solar:delivery-bold-duotone",
        kind: 'desktop'
      },
      {
        title: t('landingPage.userControlCenter'),
        description: t('landingPage.userControlCenterDesc'),
        image: "/screenshots/Manager-User-Management.png",
        icon: "solar:users-group-two-rounded-bold-duotone",
        kind: 'desktop'
      }
    ],
    salesrep: [
      {
        title: t('landingPage.storeVisits'),
        description: t('landingPage.storeVisitsDesc'),
        image: "/screenshots/Mobile-Stores.png",
        icon: "solar:map-point-bold-duotone",
        kind: 'mobile'
      },
      {
        title: t('landingPage.fieldDashboard'),
        description: t('landingPage.fieldDashboardDesc'),
        image: "/screenshots/Mobile-single-store.png",
        icon: "solar:smartphone-bold-duotone",
        kind: 'mobile'
      },
      {
        title: t('landingPage.customWorkflows'),
        description: t('landingPage.customWorkflowsDesc'),
        image: "/screenshots/mobile-notes-.png",
        icon: "solar:clipboard-bold-duotone",
        kind: 'mobile'
      },
      {
        title: t('landingPage.orderEntry'),
        description: t('landingPage.orderEntryDesc'),
        image: "/screenshots/Sales-rep-order-page-.png",
        icon: "solar:cart-large-4-bold-duotone",
        kind: 'desktop'
      }
    ],
    core: [
      {
        title: t('landingPage.earningsPayroll'),
        description: t('landingPage.earningsPayrollDesc'),
        image: "/screenshots/Payroll-Page.png",
        icon: "solar:wad-of-money-bold-duotone",
        kind: 'desktop'
      },
      {
        title: t('landingPage.profileManagement'),
        description: t('landingPage.profileManagementDesc'),
        image: "/screenshots/Profile-page.png",
        icon: "solar:user-circle-bold-duotone",
        kind: 'desktop'
      },
      {
        title: t('landingPage.storeManagement'),
        description: t('landingPage.storeManagementDesc'),
        image: "/screenshots/Sales-rep-Stores-.png",
        icon: "solar:shop-2-bold-duotone",
        kind: 'desktop'
      },
      {
        title: t('landingPage.salesRepTool'),
        description: t('landingPage.salesRepToolDesc'),
        image: "/screenshots/Sales-rep-single-store.png",
        icon: "solar:widget-bold-duotone",
        kind: 'desktop'
      }
    ]
  };

  useEffect(() => {
    // Standardize GSAP context for better cleanup
    const ctx = gsap.context(() => {
      // Hero Animations
      const heroElements = document.querySelectorAll('.hero-animate');
      if (heroElements.length > 0) {
        gsap.from(".hero-animate", {
          y: 30,
          opacity: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          clearProps: "all"
        });
      }

      if (document.querySelector('.hero-image-animate')) {
        gsap.from(".hero-image-animate", {
          scale: 0.95,
          opacity: 0,
          duration: 1.2,
          delay: 0.4,
          ease: "power2.out",
          clearProps: "all"
        });
      }

      // Feature Animations
      if (document.querySelector('.feature-card-spotlight')) {
        gsap.from(".feature-card-spotlight", {
          scrollTrigger: {
            trigger: ".feature-grid",
            start: "top 85%",
          },
          y: 40,
          opacity: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          clearProps: "all"
        });
      }

      // Content Block Animations
      const blocks = gsap.utils.toArray(".content-block");
      blocks.forEach((block: any) => {
        const animateUp = block.querySelectorAll(".animate-up");
        if (animateUp.length > 0) {
          gsap.from(animateUp, {
            scrollTrigger: {
              trigger: block,
              start: "top 80%",
            },
            y: 30,
            opacity: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: "power2.out",
            clearProps: "all"
          });
        }
      });

      // Metrics Counting Animation
      if (document.querySelector('.metric-number')) {
        gsap.from(".metric-number", {
          scrollTrigger: {
            trigger: ".metrics-container",
            start: "top 90%",
          },
          textContent: 0,
          duration: 1.5,
          ease: "power1.inOut",
          snap: { textContent: 1 },
          stagger: 0.1
        });
      }
    }, containerRef);

    return () => ctx.revert(); // Proper cleanup
  }, []);

  const goToLogin = () => navigate('/login');

  return (
    <PageContainer ref={containerRef}>
      <SEO 
        title="Home"
        description="Next-generation ERP engineered for your unique business DNA. Optimize field operations, automate tracking, and empower your team."
        keywords={['ERP', 'Enterprise', 'Field Sales', 'Logistics', 'Supply Chain', 'Automation']}
      />
      <Header>
        <Logo onClick={() => navigate('/')}>
          <LogoImg src="/assets/erp-logo.svg" alt="Nexis ERP Logo" />
        </Logo>
        <NavLinks>
          <NavLink href="#solutions">{t('landingPage.solutions')}</NavLink>
          <NavLink href="#features">{t('landingPage.features')}</NavLink>
          <NavLink href="#customization">{t('landingPage.customization')}</NavLink>
          <NavLink href="#testimonials">{t('landingPage.impact')}</NavLink>
          <LangSwitcher onClick={toggleLanguage} className="lang-btn">
            <Languages size={16} />
            {i18n.language === 'en' ? 'FR' : 'EN'}
          </LangSwitcher>
          <PrimaryButton onClick={() => setIsQuoteModalOpen(true)} style={{ padding: '0.6rem 1.25rem', fontSize: '0.95rem' }}>
            {t('landingPage.requestQuote')}
          </PrimaryButton>
          <LoginButton onClick={goToLogin}>{t('landingPage.login')}</LoginButton>
        </NavLinks>
      </Header>

      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <Badge className="hero-animate">
            <Icon icon="solar:rocket-bold-duotone" width="16" /> {t('landingPage.efficiencyBoost')}
          </Badge>
          <Headline className="hero-animate">
            {t('landingPage.heroTitle')}
            <span className="accent">
              <VariableProximity
                label={t('landingPage.engineered')}
                fromFontVariationSettings="'wght' 400, 'opsz' 9"
                toFontVariationSettings="'wght' 900, 'opsz' 40"
                containerRef={containerRef}
                radius={100}
                falloff="gaussian"
              />
            </span>
            {t('landingPage.forYourDNA')}
          </Headline>
          <Subheadline className="hero-animate">
            {t('landingPage.heroSubtitle')}
          </Subheadline>
          <ButtonGroup className="hero-animate">
            <PrimaryButton onClick={() => setIsQuoteModalOpen(true)}>
              {t('landingPage.getStarted')} <Icon icon="solar:arrow-right-bold" width="20" />
            </PrimaryButton>
            <GhostButton onClick={() => setIsDemoModalOpen(true)}>
              {t('landingPage.exploreDemo')}
            </GhostButton>
          </ButtonGroup>
        </HeroContent>
        <HeroImageWrapper className="hero-image-animate">
          <HeroProductStage />
          <FloatingCard top="6%" left="-8%">
            <IconWrapper color={ThemeColors.secondary.main} style={{ marginBottom: 0, width: 45, height: 45 }}>
              <Icon icon="solar:graph-up-bold-duotone" width="24" />
            </IconWrapper>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>+124%</div>
              <div style={{ fontSize: '0.75rem', color: '#718096' }}>{t('landingPage.efficiencyBoost')}</div>
            </div>
          </FloatingCard>
          <FloatingCard bottom="4%" right="22%">
            <IconWrapper color="#6366f1" style={{ marginBottom: 0, width: 45, height: 45 }}>
              <Icon icon="solar:smartphone-bold-duotone" width="24" />
            </IconWrapper>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>Field</div>
              <div style={{ fontSize: '0.75rem', color: '#718096' }}>Mobile-first</div>
            </div>
          </FloatingCard>
        </HeroImageWrapper>
      </HeroSection>

      {/* Trust Bar */}
      <TrustBar>
        <TrustTitle>{t('landingPage.poweringModernDistribution')}</TrustTitle>
        <LogoStrip>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Icon icon="solar:global-bold-duotone" width="32" /> <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>AGRI-GLOBAL</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Icon icon="solar:cpu-bold-duotone" width="32" /> <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>TECH-LOG</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Icon icon="solar:shield-check-bold-duotone" width="32" /> <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>SECURE-D</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Icon icon="solar:delivery-bold-duotone" width="32" /> <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>DISTRI-MAX</span>
          </div>
        </LogoStrip>
      </TrustBar>

      {/* Interface Showcase Section */}
      <SectionContainer id="solutions">
        <SectionHeader>
          <Badge className="hero-animate">
            <Icon icon="solar:mask-happly-bold-duotone" width="16" /> {t('landingPage.dualInterfaceAdvantage')}
          </Badge>
          <SectionTitle>{t('landingPage.interfaceExperience')}</SectionTitle>
          <p style={{ fontSize: '1.25rem', color: '#4a5568' }}>
            {t('landingPage.interfaceExperienceSubtitle')}
          </p>
        </SectionHeader>

        <TabContainer>
          <TabButton 
            active={activeTab === 'manager'} 
            onClick={() => { setActiveTab('manager'); setActiveShot(0); }}
          >
            <Icon icon="solar:monitor-bold-duotone" width="20" />
            {t('landingPage.managerExperience')}
          </TabButton>
          <TabButton 
            active={activeTab === 'salesrep'} 
            onClick={() => { setActiveTab('salesrep'); setActiveShot(0); }}
          >
            <Icon icon="solar:smartphone-bold-duotone" width="20" />
            {t('landingPage.salesRepMobile')}
          </TabButton>
          <TabButton 
            active={activeTab === 'core'} 
            onClick={() => { setActiveTab('core'); setActiveShot(0); }}
          >
            <Icon icon="solar:layers-bold-duotone" width="20" />
            {t('landingPage.coreArchitecture')}
          </TabButton>
        </TabContainer>

        <ProductShowcase
          items={showcaseData[activeTab]}
          activeIndex={activeShot}
          onSelect={setActiveShot}
        />
        <div style={{ textAlign: 'center', maxWidth: 640, margin: '1.75rem auto 0' }}>
          <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: ThemeColors.primary.main, marginBottom: '0.5rem' }}>
            {showcaseData[activeTab][activeShot]?.title}
          </h3>
          <p style={{ color: '#718096', fontSize: '1rem', lineHeight: 1.6 }}>
            {showcaseData[activeTab][activeShot]?.description}
          </p>
          <CustomizationBadge>
            <Icon icon="solar:magic-stick-3-bold-duotone" width="14" />
            {t('landingPage.fullyCustomizable')}
          </CustomizationBadge>
        </div>
      </SectionContainer>

      {/* Feature Deep Dive */}
      <SectionContainer id="features" style={{ background: '#f8fafc', borderRadius: '64px' }}>
        <SectionHeader>
          <SectionTitle>{t('landingPage.performanceFirst')}</SectionTitle>
          <p style={{ fontSize: '1.25rem', color: '#4a5568' }}>
            {t('landingPage.fasterDesc')}
          </p>
        </SectionHeader>

        <FeatureGrid>
          <SpotlightCard>
            <FeatureIconBox bg={ThemeColors.primary.main}>
              <Icon icon="solar:chart-2-bold-duotone" width="28" />
            </FeatureIconBox>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.75rem' }}>{t('landingPage.advancedAnalytics')}</h4>
            <p style={{ color: '#718096', lineHeight: 1.6 }}>{t('landingPage.advancedAnalyticsDesc')}</p>
          </SpotlightCard>
          <SpotlightCard>
            <FeatureIconBox bg={ThemeColors.secondary.main}>
              <Icon icon="solar:shield-user-bold-duotone" width="28" />
            </FeatureIconBox>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.75rem' }}>{t('landingPage.roleBasedAccess')}</h4>
            <p style={{ color: '#718096', lineHeight: 1.6 }}>{t('landingPage.roleBasedAccessDesc')}</p>
          </SpotlightCard>
          <SpotlightCard>
            <FeatureIconBox bg="#f59e0b">
              <Icon icon="solar:cloud-square-bold-duotone" width="28" />
            </FeatureIconBox>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.75rem' }}>{t('landingPage.offlineFirstEngine')}</h4>
            <p style={{ color: '#718096', lineHeight: 1.6 }}>{t('landingPage.offlineFirstDesc')}</p>
          </SpotlightCard>
          <SpotlightCard>
            <FeatureIconBox bg="#ec4899">
              <Icon icon="solar:rocket-bold-duotone" width="28" />
            </FeatureIconBox>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.75rem' }}>{t('landingPage.rapidDeployment')}</h4>
            <p style={{ color: '#718096', lineHeight: 1.6 }}>{t('landingPage.rapidDeploymentDesc')}</p>
          </SpotlightCard>
        </FeatureGrid>
      </SectionContainer>

      {/* Customization Section */}
      <SectionContainer id="customization">
        <ContentBlock className="content-block">
          <div className="animate-up">
            <Badge><Icon icon="solar:star-fall-bold-duotone" width="14" /> {t('landingPage.bespokeEngineering')}</Badge>
            <SectionTitle left>{t('landingPage.engineeredForDNA')}</SectionTitle>
            <p style={{ fontSize: '1.2rem', color: '#4a5568', lineHeight: '1.7', marginBottom: '2rem' }}>
              {t('landingPage.bespokeDesc')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 600 }}>
                <Icon icon="solar:check-circle-bold-duotone" width="20" color={ThemeColors.secondary.main} /> {t('landingPage.customUX')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 600 }}>
                <Icon icon="solar:check-circle-bold-duotone" width="20" color={ThemeColors.secondary.main} /> {t('landingPage.bespokeIntegrations')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 600 }}>
                <Icon icon="solar:check-circle-bold-duotone" width="20" color={ThemeColors.secondary.main} /> {t('landingPage.uniqueDataLogic')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 600 }}>
                <Icon icon="solar:check-circle-bold-duotone" width="20" color={ThemeColors.secondary.main} /> {t('landingPage.privateCloud')}
              </div>
            </div>
            <PrimaryButton onClick={() => setIsQuoteModalOpen(true)} style={{ marginTop: '2.5rem' }}>
              {t('landingPage.discussCustomBuild')} <ArrowRight size={20} />
            </PrimaryButton>
          </div>
          <div className="animate-up">
            <CustomizationProductStage />
          </div>
        </ContentBlock>
      </SectionContainer>

      {/* Infinite Customization / Potential Features */}
      <SectionContainer style={{ paddingBottom: '4rem' }}>
        <SectionHeader>
          <SectionTitle>{t('landingPage.beyondTitle')}</SectionTitle>
          <p style={{ fontSize: '1.25rem', color: '#4a5568' }}>
            {t('landingPage.beyondSubtitle')}
          </p>
        </SectionHeader>

        <FeatureGrid style={{ gap: '1.5rem' }}>
          {[
            { name: t('landingPage.aiForecasting'), icon: "solar:magic-stick-3-bold-duotone" },
            { name: t('landingPage.globalCurrency'), icon: "solar:globus-bold-duotone" },
            { name: t('landingPage.iotTracking'), icon: "solar:cpu-bold-duotone" },
            { name: t('landingPage.advancedHr'), icon: "solar:users-group-rounded-bold-duotone" },
            { name: t('landingPage.apiGateway'), icon: "solar:key-square-bold-duotone" },
            { name: t('landingPage.customReports'), icon: "solar:document-bold-duotone" },
            { name: t('landingPage.multiWarehouse'), icon: "solar:box-bold-duotone" },
            { name: t('landingPage.fleetManagement'), icon: "solar:bus-bold-duotone" }
          ].map((feat, i) => (
            <SpotlightCard key={i} style={{ 
              padding: '2rem', 
              textAlign: 'center',
              border: '1px solid #edf2f7',
              borderRadius: '24px'
            }}>
              <Icon icon={feat.icon} width="32" color={ThemeColors.primary.main} style={{ marginBottom: '1rem' }} />
              <div style={{ fontWeight: 700, color: '#2d3748' }}>{feat.name}</div>
            </SpotlightCard>
          ))}
        </FeatureGrid>
        
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <p style={{ fontStyle: 'italic', color: '#718096', marginBottom: '1.5rem', fontSize: '1.1rem' }}>
            "{t('landingPage.ifYourBusinessNeeds')}"
          </p>
          <PrimaryButton onClick={() => setIsQuoteModalOpen(true)} style={{ margin: '0 auto' }}>
            {t('landingPage.requestCustomQuote')}
          </PrimaryButton>
        </div>
      </SectionContainer>

      {/* Testimonials Carousel */}
      <TestimonialSection id="testimonials">
        <SectionHeader style={{ color: 'white' }}>
          <SectionTitle light>{t('landingPage.verifiedEnterpriseImpact')}</SectionTitle>
          <p style={{ fontSize: '1.25rem', opacity: 0.8 }}>
            {t('landingPage.joinCompanies')}
          </p>
        </SectionHeader>
        
        <Carousel 
          items={testimonialItems} 
          autoplay={true} 
          autoplayDelay={5000} 
          pauseOnHover={true} 
          loop={true}
          baseWidth={500}
        />
      </TestimonialSection>

      {/* Metrics Section */}
      <SectionContainer className="metrics-container">
        <MetricsGrid>
          <MetricBox>
            <h3><span className="metric-number">10</span>x</h3>
            <p>{t('landingPage.fasterPerformance')}</p>
          </MetricBox>
          <MetricBox>
            <h3><span className="metric-number">100</span>%</h3>
            <p>{t('landingPage.paperlessAdoption')}</p>
          </MetricBox>
          <MetricBox>
            <h3><span className="metric-number">40</span>%</h3>
            <p>{t('landingPage.supportReduction')}</p>
          </MetricBox>
          <MetricBox>
            <h3><span className="metric-number">24</span>/7</h3>
            <p>{t('landingPage.cloudReliability')}</p>
          </MetricBox>
        </MetricsGrid>
      </SectionContainer>

      {/* Final CTA */}
      <SectionContainer style={{ background: '#f8fafc', borderRadius: '48px', textAlign: 'center', maxWidth: '1100px', marginBottom: '8rem' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <SectionTitle>{t('landingPage.readyForMegaLaunch')}</SectionTitle>
          <p style={{ fontSize: '1.35rem', color: '#4a5568', marginBottom: '3rem' }}>
            {t('landingPage.joinElite')}
          </p>
          <ButtonGroup style={{ justifyContent: 'center' }}>
            <PrimaryButton onClick={() => setIsQuoteModalOpen(true)} style={{ padding: '1.25rem 3.5rem' }}>
              {t('landingPage.requestCustomQuote')}
            </PrimaryButton>
            <GhostButton onClick={() => setIsDemoModalOpen(true)} style={{ padding: '1.25rem 3.5rem' }}>
              {t('landingPage.tryFreeDemo')}
            </GhostButton>
          </ButtonGroup>
        </div>
      </SectionContainer>

      <Footer>
        <FooterGrid>
          <FooterCol>
            <Logo style={{ marginBottom: '1.5rem' }}>
              <LogoImg src="/assets/erp-logo.svg" alt="Nexis ERP" style={{ filter: 'brightness(0) invert(1)' }} />
            </Logo>
            <p style={{ color: '#94a3b8', lineHeight: 1.6 }}>
              {t('landingPage.footerDesc')}
            </p>
          </FooterCol>
          <FooterCol>
            <h4>{t('landingPage.product')}</h4>
            <ul>
              <li><a href="#solutions">{t('landingPage.solutions')}</a></li>
              <li><a href="#customization">{t('landingPage.customization')}</a></li>
              <li><a href="#features">Mobile App</a></li>
              <li><a href="#features">Analytics</a></li>
            </ul>
          </FooterCol>
          <FooterCol>
            <h4>{t('landingPage.company')}</h4>
            <ul>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/blog">Blog</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
            </ul>
          </FooterCol>
          <FooterCol>
            <h4>{t('landingPage.support')}</h4>
            <ul>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/contact">Contact Sales</Link></li>
              <li><Link to="/faq">Documentation</Link></li>
              <li><Link to="/contact">Status</Link></li>
            </ul>
          </FooterCol>
        </FooterGrid>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
          © 2026 Nexis ERP. {t('landingPage.rightsReserved')}
        </div>
      </Footer>

      <DemoRequestModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />
      <QuoteRequestModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
      />
    </PageContainer>
  );
};
